import { forwardRef } from 'react';
import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';

const { semantic } = colorTokens;

/**
 * Shared Navigation / Tabs (Figma Base).
 * Primary: pill/card tab bar for page-level navigation.
 * Secondary: underline subnav for nested sections (e.g. Funkcie).
 */
export const Tabs = forwardRef(function Tabs(
  {
    value,
    onChange,
    children,
    variant = 'scrollable',
    scrollButtons = 'auto',
    allowScrollButtonsMobile = true,
    appearance = 'Primary',
    sx,
    ...rest
  },
  ref,
) {
  const isSecondary = appearance === 'Secondary' || appearance === 'secondary';

  return (
    <MuiTabs
      ref={ref}
      value={value}
      onChange={onChange}
      variant={variant}
      scrollButtons={isSecondary ? false : scrollButtons}
      allowScrollButtonsMobile={isSecondary ? false : allowScrollButtonsMobile}
      sx={{
        ...(isSecondary
          ? {
              minHeight: 36,
              mb: 2.5,
              px: 0,
              py: 0,
              bgcolor: 'transparent',
              border: 'none',
              borderRadius: 0,
              borderBottom: `1px solid ${semantic.border.subtle}`,
              overflow: 'visible',
              '& .MuiTabs-scroller': {
                px: 0,
                maskImage: 'none',
                WebkitMaskImage: 'none',
              },
              '& .MuiTabs-flexContainer': {
                gap: 0,
                alignItems: 'stretch',
                px: 0,
              },
              '& .MuiTabs-indicator': {
                display: 'block',
                height: 2,
                bgcolor: semantic.status.brand.border,
              },
            }
          : {
              minHeight: 44,
              mb: 3,
              px: 0.5,
              py: 0.75,
              bgcolor: semantic.bg.surface,
              border: `1px solid ${semantic.border.subtle}`,
              borderRadius: '14px',
              overflow: 'hidden',
              '& .MuiTabs-scroller': {
                px: 0.5,
                maskImage: 'none',
                WebkitMaskImage: 'none',
              },
              '& .MuiTabs-flexContainer': {
                gap: 0.5,
                alignItems: 'center',
                px: 0.25,
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTabs-scrollButtons': {
                width: 36,
                flexShrink: 0,
                color: semantic.text.muted,
                '&.Mui-disabled': {
                  opacity: 0.35,
                },
              },
            }),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </MuiTabs>
  );
});

/**
 * Shared Navigation / Tab item.
 * @param {'Primary'|'Secondary'} [appearance='Primary']
 */
export const Tab = forwardRef(function Tab({
  label,
  appearance = 'Primary',
  sx,
  ...rest
}, ref) {
  const isSecondary = appearance === 'Secondary' || appearance === 'secondary';

  return (
    <MuiTab
      ref={ref}
      label={label}
      disableRipple
      sx={{
        ...(isSecondary
          ? {
              minHeight: 36,
              minWidth: 'auto',
              maxWidth: 'none',
              flexShrink: 0,
              px: 1.25,
              py: 0.75,
              mx: 0,
              borderRadius: 0,
              border: 'none',
              textTransform: 'none',
              fontFamily: typographyTokens.fontFamily,
              fontWeight: typographyTokens.fontWeightSemibold,
              fontSize: 12,
              lineHeight: '16px',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              color: semantic.text.muted,
              opacity: 1,
              '&.Mui-selected': {
                color: semantic.text.primary,
                bgcolor: 'transparent',
                borderColor: 'transparent',
              },
              '&:hover': {
                color: semantic.text.secondary,
                bgcolor: 'transparent',
              },
              '&.Mui-selected:hover': {
                bgcolor: 'transparent',
                color: semantic.text.primary,
              },
            }
          : {
              minHeight: 28,
              minWidth: 'auto',
              maxWidth: 'none',
              flexShrink: 0,
              px: 1.5,
              py: 0.5,
              mx: 0.125,
              borderRadius: `${radiusTokens.navItem}px`,
              border: '1px solid transparent',
              textTransform: 'none',
              fontFamily: typographyTokens.fontFamily,
              fontWeight: typographyTokens.fontWeightSemibold,
              fontSize: 13,
              lineHeight: '18px',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              color: semantic.text.muted,
              opacity: 1,
              '&.Mui-selected': {
                color: semantic.text.primary,
                bgcolor: semantic.bg.surfaceSelected,
                borderColor: semantic.status.brand.border,
              },
              '&:hover': {
                color: semantic.text.secondary,
                bgcolor: semantic.action.secondary.bg,
              },
              '&.Mui-selected:hover': {
                bgcolor: semantic.bg.surfaceSelected,
                color: semantic.text.primary,
              },
            }),
        ...sx,
      }}
      {...rest}
    />
  );
});

export default Tabs;
